import { useState, useRef } from 'react';
import type { ReactNode } from 'react';
import type { OverlayController } from '../bridge/types';

type UseRuntimeOverlayControllerResult = {
	/**
	 * True when an overlay is currently shown.
	 * Callers can use this to adjust layout — e.g. release a fixed height so the
	 * overlay content is not clipped, and hide the iframe underneath.
	 */
	isInlineActive: boolean;
	overlay: ReactNode;
	overlayController: OverlayController;
};

/**
 * Owns the single overlay slot shared by all bridge-triggered UI.
 *
 * All overlay content renders directly into the slot. Components that need ADS
 * modal animation — e.g. `ExternalLinkModal` — are responsible for wrapping
 * themselves in `<ModalTransition>`.
 *
 * The `affectsLayout` flag distinguishes two cases:
 * - **`affectsLayout = true`** (ThreeLOPrompt): the overlay is an inline `<Box>`
 *   that occupies space in the document flow. The iframe must be hidden and the
 *   container height released so the prompt is not clipped. `isInlineActive`
 *   reflects this.
 * - **`affectsLayout = false`** (default, ExternalLinkModal): the overlay renders
 *   into a portal via `<ModalTransition>`. The iframe remains visible at full
 *   height underneath.
 *
 * Only one overlay can be shown at a time — `show` always replaces the current
 * content.
 *
 * The `overlayController` is stable for the lifetime of the component (backed
 * by a ref) so it is safe to store in BridgeContext and capture in bridge method
 * closures without stale-closure concerns.
 */
export const useRuntimeOverlayController = (): UseRuntimeOverlayControllerResult => {
	const [overlayContent, setOverlayContent] = useState<ReactNode>(null);
	const [isOverlayOpen, setIsOverlayOpen] = useState(false);
	const [affectsLayout, setAffectsLayout] = useState(false);

	// Stable object — never recreated, always points at the current setter.
	const overlayControllerRef = useRef<OverlayController>({
		show: (content: ReactNode, willAffectsLayout = false) => {
			setOverlayContent(content);
			setAffectsLayout(willAffectsLayout);
			setIsOverlayOpen(true);
		},
		hide: () => setIsOverlayOpen(false),
	});

	const overlay = isOverlayOpen ? overlayContent : null;

	return {
		overlay,
		overlayController: overlayControllerRef.current,
		isInlineActive: affectsLayout && isOverlayOpen,
	};
};
